import { useState } from 'react';
import { useSessionStore } from '@/store/sessionStore';
import { cmd } from '@/session/sessionRuntime';

const MIN_PLAYERS = 4;
const MAX_PLAYERS = 10;

export function LobbyView(): JSX.Element {
  const players = useSessionStore((s) => s.players);
  const waitlist = useSessionStore((s) => s.waitlist);
  const self = useSessionStore((s) => s.self);

  const onlinePlayers = players.filter((p) => p.online);
  const onlineCount = onlinePlayers.length;
  const canStart = onlineCount >= MIN_PLAYERS && onlineCount <= MAX_PLAYERS;
  const tooFew = onlineCount < MIN_PLAYERS;
  const tooMany = onlineCount > MAX_PLAYERS;
  const isInWaitlist = self ? waitlist.some((w) => w.token === self.token) : false;

  let buttonLabel: string;
  if (isInWaitlist) {
    buttonLabel = '等待入场';
  } else if (canStart) {
    buttonLabel = `发起开局（${onlineCount} 人）`;
  } else if (tooFew) {
    buttonLabel = `需要 ${MIN_PLAYERS}-${MAX_PLAYERS} 名玩家（当前 ${onlineCount} 人）`;
  } else {
    buttonLabel = `最多 ${MAX_PLAYERS} 人`;
  }

  return (
    <main className="mx-auto flex max-w-md flex-col gap-4 p-4">
      <h1 className="text-center text-2xl font-bold text-pig-700">🐷 Pigwords</h1>

      <SelfPanel />

      <section data-testid="player-list" className="rounded-lg bg-white p-3 shadow">
        <h2 className="mb-2 text-sm font-semibold text-gray-600">
          在线玩家 {onlinePlayers.length}/{MAX_PLAYERS}
        </h2>
        <ul className="space-y-2">
          {players.map((p) => (
            <li
              key={p.token}
              className="flex h-14 items-center justify-between rounded border border-gray-100 px-3"
            >
              <span className="flex items-center gap-2">
                <span
                  className={`inline-block h-2 w-2 rounded-full ${
                    p.online ? 'bg-green-500' : 'bg-gray-300'
                  }`}
                  aria-label={p.online ? '在线' : '离线'}
                />
                <span className="font-medium">{truncateName(p.nickname)}</span>
                <span className="text-xs text-gray-400">#{p.token.slice(-4).toUpperCase()}</span>
              </span>
              <span className="text-xs text-gray-500">
                {p.isChief && <span className="mr-1 rounded bg-amber-100 px-1">村长</span>}
              </span>
            </li>
          ))}
          {players.length === 0 && (
            <li className="py-4 text-center text-sm text-gray-400">还没有人，邀请朋友加入</li>
          )}
        </ul>
      </section>

      {waitlist.length > 0 && (
        <section data-testid="waitlist" className="rounded-lg bg-amber-50 p-3">
          <h2 className="mb-2 text-sm font-semibold text-amber-800">
            等待席 {waitlist.length}/{MAX_PLAYERS}{isInWaitlist && '（你在等待席）'}
          </h2>
          <ul className="space-y-1 text-sm text-amber-900">
            {waitlist.map((w) => (
              <li key={w.token}>
                {truncateName(w.nickname)}{' '}
                <span className="text-xs text-amber-600">#{w.token.slice(-4).toUpperCase()}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <button
        data-testid="start-game-btn"
        disabled={!canStart || isInWaitlist}
        onClick={() => cmd.startGame()}
        className="h-14 w-full rounded-lg bg-pig-600 text-lg font-semibold text-white shadow disabled:bg-gray-300"
      >
        {buttonLabel}
      </button>
      {(tooFew || tooMany) && !isInWaitlist && (
        <p className="text-center text-xs text-gray-500">
          {MIN_PLAYERS}-{MAX_PLAYERS} 名玩家可开局
        </p>
      )}
    </main>
  );
}

function SelfPanel(): JSX.Element | null {
  const self = useSessionStore((s) => s.self);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  if (!self) return null;

  const submit = (): void => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== self.nickname && trimmed.length <= 16) {
      cmd.setNickname(trimmed);
    }
    setEditing(false);
  };

  return (
    <section className="flex items-center justify-between rounded-lg bg-pig-100 p-3">
      <div>
        <div className="text-xs text-pig-700">你的昵称</div>
        {editing ? (
          <input
            data-testid="nickname-input"
            autoFocus
            maxLength={16}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={submit}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            className="rounded border px-2 py-1 text-base"
          />
        ) : (
          <div className="text-lg font-bold">{truncateName(self.nickname)}</div>
        )}
      </div>
      {!editing && (
        <button
          data-testid="edit-nickname-btn"
          onClick={() => {
            setDraft(self.nickname);
            setEditing(true);
          }}
          className="rounded bg-white px-3 py-1 text-sm text-pig-700 shadow"
        >
          改昵称
        </button>
      )}
    </section>
  );
}

export function truncateName(name: string): string {
  // 中文按 1 字 / 西文按 1 字（简化）
  return name.length <= 8 ? name : name.slice(0, 8) + '…';
}
